import type {
  ClientMembershipEntry,
  SavedFreelancerMembership,
} from "../api/types";

/**
 * Workspace-keyed membership state held by the provider's `createStore`.
 * The Talent Pool is per-company (D9): entries never leak across workspaces.
 */
export type MembershipState = {
  companyId: string | undefined;
  entries: Record<string, ClientMembershipEntry>;
};

/**
 * An entry is "mid-flight" while its local state represents unconfirmed user
 * intent that a hydration page must not clobber:
 *   - an optimistic save that has not received its server record id yet, or
 *   - a post-409 single-id refetch that is still pending.
 * For these, the server hydration page is stale relative to local intent.
 */
const isMidFlight = (entry: ClientMembershipEntry): boolean =>
  entry.saved_freelancer_id === undefined || entry.refetchPending === true;

/**
 * Merges a bulk membership hydration page into the current map.
 *
 * Rules:
 *   - ids present only in `page` are added (fills unknown ids);
 *   - a settled (non-mid-flight) current entry is overwritten with the
 *     authoritative server data;
 *   - a mid-flight current entry (optimistic / refetch-pending) is preserved
 *     untouched so hydration never loses local intent;
 *   - ids present only in `current` are kept — the page is a partial lookup for
 *     a specific id set, so absence never means "removed".
 *
 * Pure and DOM-free (node-testable); imports only types from the api layer.
 */
export const mergeMembershipPage = (
  current: Record<string, ClientMembershipEntry>,
  page: Record<string, SavedFreelancerMembership>,
): Record<string, ClientMembershipEntry> => {
  const next: Record<string, ClientMembershipEntry> = { ...current };

  for (const id of Object.keys(page)) {
    const existing = current[id];
    if (existing && isMidFlight(existing)) {
      continue;
    }
    next[id] = { ...page[id] };
  }

  return next;
};

/**
 * Keying helper for the workspace-switch reset (D9 + FLOW edge case).
 *
 * Returns the SAME `previous` reference when the company is unchanged (a cheap
 * identity check for the caller's effect), and a fresh state with an empty
 * entries map when the company changed — dropping the previous company's
 * entries so saved state never leaks across workspaces.
 */
export const membershipForWorkspace = (
  previous: MembershipState,
  companyId: string | undefined,
): MembershipState =>
  previous.companyId === companyId ? previous : { companyId, entries: {} };

/**
 * Given the flattened list of currently-loaded freelancer profile ids and a
 * running set of ids already dispatched to the membership endpoint, returns the
 * ids that still need a lookup: present in `allIds`, absent from
 * `alreadyRequested`, de-duplicated, and order-preserving.
 *
 * Drives the per-page hydration effect (T9b):
 *   - first run → every id (nothing requested yet);
 *   - a reactive re-run with the same list → `[]` (all already requested);
 *   - a load-more (list grows) → only the appended page's ids;
 *   - a duplicate id repeated across pages is never re-requested.
 *
 * Pure and DOM-free (node-testable).
 */
export const newIdsToHydrate = (
  allIds: string[],
  alreadyRequested: ReadonlySet<string>,
): string[] => {
  const seen = new Set<string>();
  const result: string[] = [];
  for (const id of allIds) {
    if (alreadyRequested.has(id) || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
};
